import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AdminFloatingShell } from '../components/layout/AdminFloatingShell';
import { UnifiedPortalUsersPanel } from '../components/settings/UnifiedPortalUsersPanel';
import { OnlineUsersPanel } from '../components/settings/OnlineUsersPanel';
import { ActivityLogPanel } from '../components/settings/ActivityLogPanel';
import { CloudTalkAgentLogsPanel } from '../components/settings/CloudTalkAgentLogsPanel';
import { AdminHubProvider } from '../contexts/AdminHubContext';
import { useAuth } from '../contexts/AuthContext';
import {
  canAccessAdminHub,
  canManageCrmConfigInAdminHub,
  canManageDemConfigInAdminHub,
  canManageFormsConfigInAdminHub,
  canManageItConfigInAdminHub,
  resolveAdminHubActorRole,
} from '../lib/adminHubPermissions';
import { SequencesAdminPanel } from '../components/settings/SequencesAdminPanel';
import { EmailTemplatesAdminPanel } from '../components/settings/EmailTemplatesAdminPanel';
import { SynchronizeAdminPanel } from '../components/settings/SynchronizeAdminPanel';
import { SharePointConfigurationPage } from './SharePointConfigurationPage';
import { DemSettingsPage } from './dem/DemSettingsPage';
import { ApplicationFormGeneralPanel } from '../components/forms/settings/ApplicationFormGeneralPanel';
import { ApplicationFormsSettingsPage } from './forms/ApplicationFormsSettingsPage';

type AdminHubTab =
  | 'online'
  | 'activity'
  | 'users-portal'
  | 'users-forms'
  | 'sharepoint-config'
  | 'integrations'
  | 'email-templates'
  | 'sequences'
  | 'synchronize'
  | 'forms-general'
  | 'forms-config';

const TAB_ITEMS: {
  id: string;
  label: string;
  icon: string;
  children?: { id: AdminHubTab; label: string }[];
}[] = [
  { id: 'online', label: 'Online users', icon: '🟢' },
  { id: 'activity', label: 'Activity log', icon: '📜' },
  {
    id: 'users',
    label: 'User management',
    icon: '👥',
    children: [
      { id: 'users-portal', label: 'Portal' },
      { id: 'users-forms', label: 'Application Form' },
    ],
  },
  { id: 'sharepoint-config', label: 'SharePoint', icon: '📁' },
  { id: 'integrations', label: 'Integrations', icon: '🔌' },
  { id: 'email-templates', label: 'Email Templates', icon: '✉️' },
  { id: 'sequences', label: 'Sequences', icon: '🔢' },
  { id: 'synchronize', label: 'Synchronize', icon: '🔄' },
  { id: 'forms-general', label: 'General', icon: '⚙️' },
  { id: 'forms-config', label: 'Forms & deliveries', icon: '📝' },
];

function resolveAdminHubPageTitle(tab: AdminHubTab): string {
  for (const item of TAB_ITEMS) {
    if (item.id === tab) return item.label;
    const child = item.children?.find((c) => c.id === tab);
    if (child) return item.label;
  }
  return TAB_ITEMS[0]?.label ?? 'Settings';
}

export function AdminHubPage() {
  const { portalAccess } = useAuth();
  const actorRole = resolveAdminHubActorRole(portalAccess);
  const [tab, setTab] = useState<AdminHubTab>('online');

  if (!canAccessAdminHub(portalAccess) || !actorRole) {
    return <Navigate to="/" replace />;
  }

  const canSharePoint =
    canManageCrmConfigInAdminHub(actorRole) ||
    canManageItConfigInAdminHub(actorRole) ||
    canManageFormsConfigInAdminHub(actorRole);

  return (
    <AdminHubProvider actorRole={actorRole}>
      <AdminFloatingShell
        items={TAB_ITEMS}
        activeId={tab}
        onNavigate={(id) => setTab(id as AdminHubTab)}
        pageTitle={resolveAdminHubPageTitle(tab)}
      >
        <div className="admin-hub-page admin-hub-page--floating">
          {tab === 'online' && (
            <div role="tabpanel">
              <OnlineUsersPanel />
            </div>
          )}

          {tab === 'activity' && (
            <div role="tabpanel" className="admin-hub-activity-stack">
              <ActivityLogPanel />
              <CloudTalkAgentLogsPanel />
            </div>
          )}

          {(tab === 'users-portal' || tab === 'users-forms') && (
            <div role="tabpanel">
              <UnifiedPortalUsersPanel
                actorRole={actorRole}
                matrixTab={tab === 'users-forms' ? 'forms' : 'portal'}
              />
            </div>
          )}

          {tab === 'sharepoint-config' && (
            <div role="tabpanel" className="admin-hub-sharepoint-config">
              {canSharePoint ? (
                <SharePointConfigurationPage />
              ) : (
                <p className="settings-empty">You cannot manage SharePoint configuration.</p>
              )}
            </div>
          )}

          {tab === 'integrations' && (
            <div role="tabpanel" className="admin-hub-integrations-config">
              {canManageDemConfigInAdminHub(actorRole) ? (
                <DemSettingsPage embeddedInAdminHub onlySection="integrations" />
              ) : (
                <p className="settings-empty">You cannot manage Integrations.</p>
              )}
            </div>
          )}

          {tab === 'synchronize' && (
            <div role="tabpanel" className="admin-hub-synchronize-config">
              {canManageDemConfigInAdminHub(actorRole) ? (
                <SynchronizeAdminPanel />
              ) : (
                <p className="settings-empty">You cannot manage Synchronize.</p>
              )}
            </div>
          )}

          {tab === 'forms-general' && (
            <div role="tabpanel" className="admin-hub-forms-general">
              {canManageFormsConfigInAdminHub(actorRole) ? (
                <ApplicationFormGeneralPanel />
              ) : (
                <p className="settings-empty">You cannot manage general form settings.</p>
              )}
            </div>
          )}

          {tab === 'forms-config' && (
            <div role="tabpanel" className="admin-hub-forms-config">
              {canManageFormsConfigInAdminHub(actorRole) ||
              canManageDemConfigInAdminHub(actorRole) ? (
                <ApplicationFormsSettingsPage embeddedInAdminHub />
              ) : (
                <p className="settings-empty">You cannot manage forms & deliveries.</p>
              )}
            </div>
          )}

          {tab === 'sequences' && (
            <div role="tabpanel" className="admin-hub-sequences-config">
              {canManageDemConfigInAdminHub(actorRole) ||
              canManageCrmConfigInAdminHub(actorRole) ||
              canManageItConfigInAdminHub(actorRole) ? (
                <SequencesAdminPanel />
              ) : (
                <p className="settings-empty">You cannot manage sequences.</p>
              )}
            </div>
          )}

          {tab === 'email-templates' && (
            <div role="tabpanel" className="admin-hub-email-templates-config">
              {canManageCrmConfigInAdminHub(actorRole) ||
              canManageItConfigInAdminHub(actorRole) ||
              canManageDemConfigInAdminHub(actorRole) ? (
                <EmailTemplatesAdminPanel />
              ) : (
                <p className="settings-empty">You cannot manage email templates.</p>
              )}
            </div>
          )}
        </div>
      </AdminFloatingShell>
    </AdminHubProvider>
  );
}
